import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface TrendDataPoint {
  date: string;
  value: number | null;
  protocol: string | null;
}

interface TrendChartProps {
  data: TrendDataPoint[];
  biomarkerName: string;
  optimalLow?: number;
  optimalHigh?: number;
  unit?: string;
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatProtocol(protocol: string | null): string {
  if (!protocol) return 'Unknown';
  return protocol.charAt(0).toUpperCase() + protocol.slice(1);
}

interface TooltipPayloadItem {
  payload: TrendDataPoint;
  value: number | null;
}

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const pt = payload[0];
  const data = pt.payload;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-navy">
        {new Date(data.date).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })}
      </p>
      <p className="mt-1 text-sm font-semibold text-copper">
        {pt.value != null ? pt.value : 'N/A'}
      </p>
      {data.protocol && (
        <p className="mt-0.5 text-xs text-gray-500">
          Protocol: {formatProtocol(data.protocol)}
        </p>
      )}
    </div>
  );
}

export function TrendChart({
  data,
  biomarkerName,
  optimalLow,
  optimalHigh,
  unit,
}: TrendChartProps) {
  // Filter to only points that have a value
  const validData = data.filter((d) => d.value != null);

  if (validData.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <TrendingUp className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-500">
              Upload more lab results to see trends
            </p>
            <p className="mt-1 text-xs text-gray-400">
              We need at least one data point to chart {biomarkerName}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate Y-axis domain with padding
  const values = validData.map((d) => d.value as number);
  const allValues = [
    ...values,
    ...(optimalLow != null ? [optimalLow] : []),
    ...(optimalHigh != null ? [optimalHigh] : []),
  ];
  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const padding = (maxVal - minVal) * 0.15 || maxVal * 0.1 || 1;
  const yMin = Math.floor(minVal - padding);
  const yMax = Math.ceil(maxVal + padding);

  return (
    <Card>
      <CardContent>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-navy">{biomarkerName}</h3>
            {unit && <p className="text-xs text-gray-400">Measured in {unit}</p>}
          </div>
          {optimalLow != null && optimalHigh != null && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="inline-block h-3 w-3 rounded-sm bg-emerald-100 border border-emerald-300" />
              Optimal range: {optimalLow} - {optimalHigh}
            </div>
          )}
        </div>
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={validData} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDateLabel}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
              />
              <YAxis
                domain={[yMin, yMax]}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <Tooltip content={<CustomTooltip />} />
              {optimalLow != null && optimalHigh != null && (
                <ReferenceArea
                  y1={optimalLow}
                  y2={optimalHigh}
                  fill="#22c55e"
                  fillOpacity={0.08}
                  stroke="#22c55e"
                  strokeOpacity={0.15}
                />
              )}
              <Line
                type="monotone"
                dataKey="value"
                stroke="#c47d2e"
                strokeWidth={2.5}
                dot={{ r: 5, fill: '#c47d2e', stroke: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 7, fill: '#c47d2e', stroke: '#fff', strokeWidth: 2 }}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
